import { Controller } from "@hotwired/stimulus"

// The task stack at the top of the composer (paperwork_tasks/_task_dock): one
// task at a time, its one-click answers always in reach; opened, the task's
// own action card. Open or folded is remembered for the session, so the stack
// keeps it when it redraws after an answer.
//
// Fast by design: the moment an answer is sent the task steps aside and the
// next one shows, without waiting for the server. The stack redraws itself
// when the task closes (a per-viewer broadcast); if the answer fails, or the
// server keeps it only as a note, the task comes back. A number key picks
// that answer on the task in front: one press, one answer, and none while an
// answer is still on its way.
//
// The broadcast replaces the whole stack (PaperworkProcess#broadcast_task_docks),
// so words half typed into a task that still waits, and which task was in
// front, are carried into the new stack.
//
// Scrolling up the timeline tucks the stack down to its question, so the page
// can be read; it comes back at the bottom of the page or from its chevron.
// It tucks only far enough from the bottom that its own shrinking cannot pull
// the page down to the bottom again.
export default class extends Controller {
  static targets = ["card"]
  static STORAGE_KEY = "task-dock-open"
  // Focus on one of these, outside the stack, means a key is meant for it.
  static ELSEWHERE = "input, textarea, select, a, button, summary, [contenteditable=''], [contenteditable='true'], " +
    "[role='radio'], [role='option'], [role='menuitem'], [role='tab'], [role='textbox'], dialog"
  static TYPING = "input, textarea, select, [contenteditable=''], [contenteditable='true']"
  // How far up a reader scrolls before the stack tucks, and how near the bottom brings it back.
  static TUCK_AFTER = 40
  static UNTUCK_WITHIN = 48

  connect() {
    this.pending = false
    this.setOpen(this.rememberedOpen())
    this.carryOver = this.carryOver.bind(this)
    document.addEventListener("turbo:before-stream-render", this.carryOver)
    this.lastY = window.scrollY || 0
    this.upTravel = 0
    this.scrolled = this.scrolled.bind(this)
    window.addEventListener?.("scroll", this.scrolled, { passive: true })
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.carryOver)
    window.removeEventListener?.("scroll", this.scrolled)
  }

  scrolled() {
    const y = window.scrollY || 0
    const delta = y - this.lastY
    this.lastY = y
    this.upTravel = delta < 0 ? this.upTravel - delta : 0
    const root = document.documentElement
    const fromBottom = (root.scrollHeight || 0) - (y + (window.innerHeight || 0))
    if (fromBottom < this.constructor.UNTUCK_WITHIN) return this.setTucked(false)
    if (this.upTravel < this.constructor.TUCK_AFTER || fromBottom < (this.element.offsetHeight || 0) + 120) return
    const focused = document.activeElement
    if (focused && focused !== document.body && this.element.closest("#paperwork_event_form")?.contains(focused)) return
    this.setTucked(true)
  }

  setTucked(tucked) {
    this.element.toggleAttribute("data-tucked", tucked)
  }

  next() { this.show(this.frontIndex() + 1) }
  prev() { this.show(this.frontIndex() - 1) }

  show(index) {
    const cards = this.waitingCards()
    if (cards.length === 0) return
    const front = cards[(index + cards.length) % cards.length]
    this.cardTargets.forEach((card) => { card.hidden = card !== front })
  }

  // Where the task in front stands among those still waiting.
  frontIndex() {
    return Math.max(0, this.waitingCards().findIndex((card) => !card.hidden))
  }

  toggle() {
    if (this.element.hasAttribute("data-tucked")) return this.setTucked(false)
    const open = !this.element.hasAttribute("data-open")
    this.setOpen(open)
    try { sessionStorage.setItem(this.constructor.STORAGE_KEY, open ? "1" : "0") } catch (_) { /* storage unavailable */ }
  }

  setOpen(open) {
    this.element.toggleAttribute("data-open", open)
    this.element.querySelectorAll(".task-dock__full").forEach((full) => { full.hidden = !open })
    this.element.querySelectorAll(".task-dock__quick").forEach((quick) => { quick.hidden = open })
    this.element.querySelectorAll(".task-dock__fold").forEach((fold) => fold.setAttribute("aria-expanded", String(open)))
  }

  rememberedOpen() {
    try { return sessionStorage.getItem(this.constructor.STORAGE_KEY) === "1" } catch (_) { return false }
  }

  // An answer went out: the next task takes the front at once.
  sent(event) {
    if (!this.answering(event)) return
    const card = event.target.closest("[data-task-dock-target='card']")
    if (!card) return
    this.pending = true
    const index = this.frontIndex()
    card.dataset.sent = ""
    if (this.waitingCards().length > 0) this.show(index)
    else card.classList.add("is-sending")
  }

  // An answer that did not go through puts its task back in front, and so
  // does one the server kept only as a note or refused (X-Task-Action): the
  // task is still open. One that took effect waits for the redrawn stack.
  settled(event) {
    if (!this.answering(event)) return
    this.pending = false
    const outcome = event.detail?.fetchResponse?.response?.headers?.get?.("X-Task-Action")
    if (event.detail?.success && outcome !== "note" && outcome !== "failed") return
    const card = event.target.closest("[data-task-dock-target='card']")
    if (!card) return
    delete card.dataset.sent
    card.classList.remove("is-sending")
    this.cardTargets.forEach((other) => { other.hidden = other !== card })
  }

  // 1–9 picks the matching answer on the task in front, and E opens it, when
  // the key is meant for the stack: not a held key repeating, not while an
  // answer is on its way, and only with focus in the stack, or on nothing in
  // particular while the stack is on screen.
  key(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || !/^[1-9eE]$/.test(event.key)) return
    if (event.repeat || this.pending || !this.keyIsForStack(event)) return
    const card = this.cardTargets.find((candidate) => !candidate.hidden && !("sent" in candidate.dataset))
    if (/^[eE]$/.test(event.key)) {
      const open = card?.querySelector(".task-dock__question")
      if (!open) return
      event.preventDefault()
      open.click()
      return
    }
    // Folded, the numbered rows; opened, the card's own suggested answers in order.
    const pick = [...(card?.querySelectorAll(`[data-task-dock-key="${event.key}"]`) || [])].find((row) => row.offsetParent !== null) ||
      [...(card?.querySelectorAll(".answer-pick") || [])].filter((row) => row.offsetParent !== null)[Number(event.key) - 1]
    if (!pick) return
    event.preventDefault()
    pick.click()
  }

  keyIsForStack(event) {
    const target = event.target && typeof event.target.closest === "function" ? event.target : null
    const focused = target && target !== document.body && target !== document.documentElement ? target : null
    if (focused && this.element.contains(focused)) return !focused.closest(this.constructor.TYPING)
    if (focused?.closest(this.constructor.ELSEWHERE)) return false
    if (document.querySelector("dialog[open], .modal.modal-open, .modal[open]")) return false
    return this.onScreen()
  }

  onScreen() {
    const rect = this.element.getBoundingClientRect?.()
    if (!rect || (rect.width === 0 && rect.height === 0)) return false
    const height = window.innerHeight || document.documentElement?.clientHeight || 0
    return rect.bottom > 0 && rect.top < height
  }

  // Before a broadcast replaces the stack, note the words typed into each
  // waiting task (and where the caret was) and the task in front; after it,
  // put them back on the same tasks, matched by their form's id.
  carryOver(event) {
    const stream = event.target
    const slot = this.element.closest(".task-dock-slot")
    if (!slot?.id || stream?.getAttribute?.("target") !== slot.id) return
    const typed = []
    this.element.querySelectorAll("form[id] input[type='text'], form[id] textarea").forEach((field) => {
      if (!field.name || field.value === field.defaultValue) return
      const focused = document.activeElement === field
      typed.push({ form: field.form.id, name: field.name, value: field.value, focused,
        start: focused ? field.selectionStart : null, end: focused ? field.selectionEnd : null })
    })
    const frontCard = this.cardTargets.find((card) => !card.hidden && !("sent" in card.dataset))
    const front = frontCard && this.cardTargets.indexOf(frontCard) > 0 ? frontCard.querySelector("form[id]")?.id : null
    if (typed.length === 0 && !front) return
    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      restoreCarried(document.getElementById(slot.id), { typed, front })
    }
  }

  waitingCards() {
    return this.cardTargets.filter((card) => !("sent" in card.dataset))
  }

  answering(event) {
    return String(event.target.action || "").includes("/task_action")
  }
}

// Puts carried words back into the redrawn stack and brings their task (or
// the one that was in front) forward.
function restoreCarried(slot, { typed, front }) {
  if (!slot) return
  const byId = (id) => slot.querySelector(`[id="${String(id).replace(/"/g, '\\"')}"]`)
  let focusCard = null
  typed.forEach(({ form, name, value, focused, start, end }) => {
    const field = [...(byId(form)?.querySelectorAll("input, textarea") || [])].find((candidate) => candidate.name === name)
    if (!field) return
    field.value = value
    field.dispatchEvent(new Event("input", { bubbles: true }))
    if (!focused) return
    focusCard = field.closest("[data-task-dock-target='card']")
    field.focus({ preventScroll: true })
    if (start !== null) { try { field.setSelectionRange(start, end) } catch (_) { /* not a text field */ } }
  })
  const card = focusCard || (front && byId(front)?.closest("[data-task-dock-target='card']"))
  if (!card) return
  slot.querySelectorAll("[data-task-dock-target='card']").forEach((other) => { other.hidden = other !== card })
}
